import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Popover,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ODDS_HISTORY_API_URL } from '../config';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { useMarketsBooks, setMarketsBooks } from '../marketsBooks';

// Same host as odds-history; alternates aren't ingested, so this one hits the server.
const ALT_ODDS_API_URL = ODDS_HISTORY_API_URL.replace(/odds-history\/?$/, 'alt-odds');

// ══════════════════════════════════════════════════════════
//  GROUPING — works off the bets snapshot the client already holds.
//  Pass playerId to get one player's props; omit it for a game's
//  non-player markets (spreads, totals, team totals, moneylines).
// ══════════════════════════════════════════════════════════

// Display order for the side columns; anything unknown falls in after these.
// Pick'em / DFS-style books: fixed-payout multipliers rather than true two-way
// prices, so they distort "best price". Excluded from this modal only.
const PICKEM_BOOK_RE = /prizepicks|underdog|pick\s?6|sleeper|props\s?builder|dabble|betr\b|chalkboard|parlayplay/i;
export const isPickemBook = (name) => PICKEM_BOOK_RE.test(String(name || ''));

const SIDE_ORDER = ['Over', 'Under', 'Away', 'Home', 'Draw', 'Yes', 'No'];
// Sides that belong to the whole game rather than to one team.
const NEUTRAL_SIDES = new Set(['over', 'under', 'yes', 'no']);
const sameTeam = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
const isTeamSide = (side) => !NEUTRAL_SIDES.has(String(side || '').trim().toLowerCase());
const sideRank = (s) => {
  const i = SIDE_ORDER.indexOf(s);
  return i === -1 ? SIDE_ORDER.length : i;
};

export function buildMarkets(bets, { fixtureId, playerId = null, bookLogos = {}, teamName = null, teamSide = null }) {
  const groups = new Map();

  (bets || []).forEach((bet) => {
    if (!bet || bet.fixture_id !== fixtureId || bet.price == null) return;
    if (playerId ? bet.playerId !== playerId : !!bet.playerId) return;
    if (isPickemBook(bet.bookName)) return;

    const rawSide = bet.side || 'Over';

    // Team panel: keep only that team's side of spreads/moneylines, and only
    // that team's team-total. Game-wide markets (totals) stay whole.
    // NB the snapshot labels team sides 'Away'/'Home' (the alt grid from the
    // server uses team names), so this filters on the side, not the name.
    const teamOwner = !playerId && !isTeamSide(rawSide) ? (bet.selection || '') : '';
    if (!playerId) {
      if (teamSide && isTeamSide(rawSide) && rawSide !== teamSide) return;
      if (teamName && teamOwner && !sameTeam(teamOwner, teamName)) return;
    }

    // Team totals share one market id across both teams, so the team has to be
    // part of the key or the two teams' books collide on one row.
    const key = `${bet.market}||${bet.periodType || ''}||${teamOwner}`;
    if (!groups.has(key)) {
      groups.set(key, {
        market: teamOwner && !(teamName || teamSide)
          ? `${teamOwner} ${bet.marketName || bet.market}`
          : (bet.marketName || bet.market),
        market_id: bet.market,
        periodType: bet.periodType || '',
        teamOwner,
        sides: new Set(),
        books: new Map(),
      });
    }
    const g = groups.get(key);
    // Yes-only player markets (anytime/first/last TD scorer) aren't classified as
    // player props server-side, so their side comes through as Away/Home from the
    // team branch of transformRecord. For a player they only have one real side.
    let side = bet.side || 'Over';
    if (playerId && side !== 'Over' && side !== 'Under') side = 'Over';
    g.sides.add(side);

    // One entry per book per line. A book can hang its main on a different line
    // for each side (Over 9.5 / Under 10.5), and those prices must not be
    // compared as if they were on the same line.
    const points = bet.points == null ? null : Number(bet.points);
    const lineKey = lineKeyOf(side, points);
    const entryKey = `${bet.bookName}||${lineKey}`;
    if (!g.books.has(entryKey)) {
      g.books.set(entryKey, {
        book: bet.bookName,
        logo: bookLogos[bet.bookName] || '',
        line: points,
        lineKey,
        prices: {},
      });
    }
    g.books.get(entryKey).prices[side] = Number(bet.price);
  });

  return [...groups.values()].map((g) => {
    let sides = [...g.sides].sort((a, b) => sideRank(a) - sideRank(b));
    // One-sided market → label it "Yes" rather than a misleading "Over"
    if (sides.length === 1 && sides[0] === 'Over') {
      sides = ['Yes'];
      g.books.forEach((b) => { b.prices.Yes = b.prices.Over; delete b.prices.Over; });
    }
    const books = [...g.books.values()].sort((a, b) => a.book.localeCompare(b.book));

    return summarizeMarket({
      market: g.market,
      market_id: g.market_id,
      periodType: g.periodType,
      teamOwner: g.teamOwner,
      sides,
      books,
    });
  }).sort((a, b) => String(a.market).localeCompare(String(b.market)));
}

// Key a quote by the line it sits on. Team sides quote one spread with opposite
// signs (ATL -4.5 / GB +4.5), so Home lines flip to the Away view; everything
// else keys on its own number.
function lineKeyOf(side, points) {
  if (points == null) return null;
  return side === 'Home' ? -points : points;
}

// Best price for one side among `quotes` ({ book, logo, price }). Every book at
// the top price is kept: a tie credited to whichever book sorts first is how
// the panel ended up naming a different book than the grid.
export function pickBest(side, quotes) {
  let price = null;
  quotes.forEach((q) => { if (q.price != null && (price == null || q.price > price)) price = q.price; });
  const winners = price == null ? [] : quotes.filter((q) => q.price === price);
  return {
    side,
    price,
    books: winners.map((q) => q.book),
    logos: winners.map((q) => q.logo).filter(Boolean),
  };
}

// Consensus line + best price per side. `allowed` (a Set of book names) limits
// it to the books the alt grid will show, so the two panes agree on who's in.
export function summarizeMarket(m, allowed = null) {
  const books = allowed ? m.books.filter((b) => allowed.has(b.book)) : m.books;

  // Consensus line = the line most books are on. Best prices are compared
  // only among books at that line, so it stays apples-to-apples.
  const tally = new Map();
  books.forEach((b) => { if (b.lineKey != null) tally.set(b.lineKey, (tally.get(b.lineKey) || 0) + 1); });
  let lineKey = null;
  let topCount = 0;
  tally.forEach((n, key) => { if (n > topCount) { topCount = n; lineKey = key; } });

  const atLine = books.filter((b) => b.lineKey === lineKey);
  const best = m.sides.map((side) => pickBest(side, atLine.map((b) => ({
    book: b.book, logo: b.logo, price: b.prices[side] ?? null,
  }))));

  return {
    ...m,
    line: atLine.length ? atLine[0].line : null,
    lineCount: topCount,
    best,
    books,
    book_count: new Set(books.map((b) => b.book)).size,
  };
}

// Re-rank a market's best prices from a freshly fetched alt grid, at the line
// the panel shows. Only sides the grid names the same way are touched: team
// sides are Away/Home in the snapshot but team names in the grid.
export function bestFromGrid(market, grid, logoOf) {
  const row = (grid?.rows || []).find((r) => r.line === market.line);
  if (!row) return market.best;
  const gridSides = new Set(grid.sides || []);
  return market.best.map((b) => {
    if (!gridSides.has(b.side)) return b;
    const quotes = Object.entries(row.books || {})
      .filter(([book]) => !isPickemBook(book))
      .sort(([a], [z]) => a.localeCompare(z))
      .map(([book, bySide]) => ({ book, logo: logoOf(book), price: bySide?.[b.side]?.price ?? null }));
    return pickBest(b.side, quotes);
  });
}

// ══════════════════════════════════════════════════════════
//  HOOK — markets are computed by the caller, so there's no fetch here
// ══════════════════════════════════════════════════════════

export function useMarkets() {
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [marketsData, setMarketsData] = useState([]);
  const [marketsMeta, setMarketsMeta] = useState(null);

  const openMarkets = useCallback((meta, markets) => {
    setMarketsMeta(meta || null);
    setMarketsData(markets || []);
    setMarketsOpen(true);
  }, []);

  const closeMarkets = useCallback(() => setMarketsOpen(false), []);

  return { marketsOpen, marketsData, marketsMeta, openMarkets, closeMarkets };
}

// ══════════════════════════════════════════════════════════
//  MODAL
// ══════════════════════════════════════════════════════════

const fmtEventStart = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(',', '');
};


// "Player Passing Touchdowns" → "Passing Touchdowns" (the player is in the header)
const shortMarket = (m) => String(m || '').replace(/^Player\s+/i, '');

// Conditional formatting: how far a price is from the best on its line, in
// implied-probability points. 0.5pt is roughly 2c of juice, so the ramp is
// tight — most books sit within a point of each other.
const toProb = (american) => (american < 0 ? -american / (-american + 100) : 100 / (american + 100));
const HEAT = [
  { gap: 0.4, color: '#d8d8d8' },
  { gap: 1.0, color: '#b0b0b0' },
  { gap: 2.0, color: '#8a8a8a' },
  { gap: 4.0, color: '#6b6b6b' },
];
const heatColor = (price, best) => {
  if (price == null || best == null) return '#aaa';
  const gap = (toProb(price) - toProb(best)) * 100;
  const step = HEAT.find((h) => gap <= h.gap);
  return step ? step.color : '#555';
};

const marketKey = (m) => (m ? `${m.market_id}||${m.periodType}||${m.teamOwner || ''}` : '');

// Right-hand pane: consensus line + best price per side, then the alt grid.
function MarketDetail({ market, meta, books, price, onGrid }) {
  // No header strip here: the left panel already shows the market, line and
  // best prices for the selected row, so the space goes to the grid instead.
  return <AltGrid market={market} meta={meta} books={books} price={price} sides={market.sides} onGrid={onGrid} />;
}

// Logo(s) for a best price. Ties show the first book plus a count; the
// tooltip names them all.
function BestLogos({ logos, size = 12 }) {
  if (!logos?.length) return null;
  return (
    <>
      <img src={logos[0]} alt="" style={{ ...LOGO_STYLE, width: size, height: size }} />
      {logos.length > 1 && (
        <span style={{ fontSize: '0.6rem', color: '#777' }}>+{logos.length - 1}</span>
      )}
    </>
  );
}

const SELECT_STYLE = {
  width: '100%',
  backgroundColor: '#222',
  color: '#eee',
  border: '1px solid #333',
  borderRadius: 4,
  padding: '5px 8px',
  fontSize: '0.8rem',
  fontFamily: 'inherit',
};


// ══════════════════════════════════════════════════════════
//  ALT GRID — lines × books for one market. Alternates aren't ingested,
//  so this fetches them on demand (one cached request per market).
// ══════════════════════════════════════════════════════════

function AltGrid({ market, meta, books: allowedBooks, price, sides, onGrid }) {
  const [data, setData] = useState(null);
  // Callback ref on the main-line row: centre it in the scroll box when the grid
  // renders (and when the side or market changes), so it opens at the lines
  // people actually bet rather than the top of the ladder.
  const mainRowRef = useCallback((node) => {
    if (!node) return;
    const box = node.closest('[data-grid-scroll]');
    if (!box) return;
    box.scrollTop = Math.max(0, node.offsetTop - (box.clientHeight / 2) + (node.offsetHeight / 2));
  }, []);
  const [error, setError] = useState(false);
  const [side, setSide] = useState(sides[0] || 'Over');

  // Lineless markets (moneyline, anytime/first/last TD scorer) have no ladder to
  // fetch — the snapshot already holds every book's price, so build the one-row
  // grid locally and skip the round-trip.
  const hasLines = market.books.some((b) => b.line != null);
  const localData = hasLines ? null : {
    sides: market.sides,
    books: market.books.map((b) => b.book),
    rows: [{
      line: null,
      is_main: true,
      books: Object.fromEntries(market.books.map((b) => [
        b.book,
        Object.fromEntries(Object.entries(b.prices).map(([sd, p]) => [sd, { price: p, is_main: true }])),
      ])),
    }],
  };

  useEffect(() => {
    if (!hasLines) return undefined;
    let cancelled = false;
    setData(null); setError(false);
    // The modal's own book list (see marketsBooks.js); otherwise every book
    // quoting this market in the snapshot.
    const bookList = allowedBooks?.length ? allowedBooks : [...new Set(market.books.map((b) => b.book))];
    const params = new URLSearchParams({
      fixture_id: meta?.fixture_id || '',
      market_id: market.market_id,
      player_id: meta?.player_id || '',
      sportsbooks: bookList.filter((b) => !isPickemBook(b)).join(','),
    });
    fetch(`${ALT_ODDS_API_URL}?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) { setData(d); onGrid?.(d); } })
      .catch((e) => { console.error('[ALT-ODDS] Fetch error:', e); if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [market.market_id, meta, hasLines, allowedBooks]);   // eslint-disable-line react-hooks/exhaustive-deps

  const grid = localData || data;

  if (error) {
    return <Box sx={GRID_WRAP}><Typography sx={EMPTY_SX}>Could not load alternates</Typography></Box>;
  }
  if (!grid) {
    return <Box sx={{ ...GRID_WRAP, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={18} sx={{ color: '#555' }} />
    </Box>;
  }

  // On a team panel, drop the other team's side so there's no Away/Home toggle.
  // Server data labels sides with team names; locally built grids came from
  // buildMarkets, which already filtered to the clicked team's side.
  const allSides = grid.sides?.length ? grid.sides : sides;
  const teamFiltered = meta?.teamName && !localData
    ? allSides.filter((sd) => !isTeamSide(sd) || sameTeam(sd, meta.teamName))
    : allSides;
  const gridSides = teamFiltered.length ? teamFiltered : allSides;
  const active = gridSides.includes(side) ? side : gridSides[0];
  const books = (grid.books || []).filter((b) => !isPickemBook(b));
  const rows = grid.rows || [];

  // Main lines are per book AND per side: on a spread, GB's main +4.5 must not
  // mark the 4.5 row while you're looking at ATL. Any book whose main line for
  // the active side is this row earns the dot; the most-agreed line is the one
  // the grid centres on.
  let centreLine = null;
  let centreVotes = 0;
  rows.forEach((row) => {
    let votes = 0;
    books.forEach((b) => { if (row.books?.[b]?.[active]?.is_main) votes += 1; });
    if (votes > centreVotes) { centreVotes = votes; centreLine = row.line; }
  });

  if (!rows.length || !books.length) {
    return <Box sx={GRID_WRAP}><Typography sx={EMPTY_SX}>No alternates offered</Typography></Box>;
  }

  return (
    <Box sx={GRID_WRAP}>
      {/* Side toggle (bigger tap targets on phones) */}
      {gridSides.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8, flexShrink: 0 }}>
          {gridSides.map((sd) => (
            <Box key={sd} onClick={() => setSide(sd)} sx={{
              px: { xs: 1.5, sm: 1 }, py: { xs: 0.6, sm: 0.2 }, fontSize: { xs: '0.8rem', sm: '0.7rem' },
              cursor: 'pointer', borderRadius: '3px',
              color: sd === active ? '#fff' : '#777',
              backgroundColor: sd === active ? '#2a2a2a' : 'transparent',
              '&:hover': { color: '#fff' },
            }}>{sd}</Box>
          ))}
        </Box>
      )}

      <Box sx={SCROLL_SX} data-grid-scroll>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...GRID_TH, position: 'sticky', left: 0, zIndex: 3, textAlign: 'left', minWidth: 54 }}>Line</th>
              <th style={{ ...GRID_TH, position: 'sticky', left: 54, zIndex: 3, borderRight: '1px solid #2a2a2a', minWidth: 64 }}>Best</th>
              {books.map((b) => (
                <th key={b} style={GRID_TH} title={b}>
                  {meta?.bookLogos?.[b]
                    ? <img src={meta.bookLogos[b]} alt={b} style={LOGO_STYLE} />
                    : <span style={{ fontSize: '0.6rem' }}>{b.slice(0, 3)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // Best price on this line, for highlighting
              const best = pickBest(active, books.map((b) => ({
                book: b, logo: meta?.bookLogos?.[b] || '', price: row.books?.[b]?.[active]?.price ?? null,
              })));
              const bestPrice = best.price;
              // "Main" is per side: on a spread, GB's main +4.5 must not mark the
              // 4.5 row as main while you're looking at ATL.
              let isMainRow = false;
              books.forEach((b) => { if (row.books?.[b]?.[active]?.is_main) isMainRow = true; });
              return (
                <tr key={row.line == null ? 'ml' : row.line}
                  ref={row.line === centreLine ? mainRowRef : null}
                  style={{ borderTop: '1px solid #1d1d1d' }}>
                  <td style={{
                    ...GRID_TD, ...NUM_STYLE, position: 'sticky', left: 0, zIndex: 2,
                    backgroundColor: '#151515', textAlign: 'left',
                    color: isMainRow ? '#fff' : '#999',
                  }}>
                    {row.line == null ? '—' : row.line}
                    {isMainRow && row.line != null ? <span style={{ color: '#ff1744' }}> •</span> : null}
                  </td>
                  <td style={{
                    ...GRID_TD, ...NUM_STYLE, position: 'sticky', left: 54, zIndex: 2,
                    backgroundColor: '#151515', borderRight: '1px solid #2a2a2a',
                    color: bestPrice == null ? '#3a3a3a' : '#aaa',
                  }} title={best.books.join(', ')}>
                    {bestPrice == null ? '·' : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                        {price(bestPrice)}
                        <BestLogos logos={best.logos} />
                      </Box>
                    )}
                  </td>
                  {books.map((b) => {
                    const cell = row.books?.[b]?.[active];
                    const isBest = cell?.price != null && cell.price === bestPrice;
                    return (
                      <td key={b} style={{
                        ...GRID_TD, ...NUM_STYLE,
                        // Shaded by distance from the best price on this line;
                        // the green box still marks the winner.
                        color: cell?.price == null ? '#3a3a3a' : heatColor(cell.price, bestPrice),
                        // Same best-odds treatment as the Odds/Props grids
                        boxShadow: isBest ? 'inset 0 0 0 2px #00cc00' : 'none',
                        borderRadius: isBest ? 3 : 0,
                      }}>
                        {cell?.price == null ? '·' : price(cell.price)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

const GRID_WRAP = { backgroundColor: '#151515', px: { xs: 1, sm: 2 }, py: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };

// Phones in either orientation get a full-screen sheet instead of a floating card
const PHONE_MQ = '(max-width:600px), (max-height:500px)';
const PHONE_PAPER = { backgroundColor: '#1a1a1a', color: '#ffffff', backgroundImage: 'none' };

// One scroll container for both axes, so the horizontal bar stays pinned to the
// bottom of the pane instead of the bottom of a very tall table. Chrome hides
// overlay scrollbars until you scroll, so they're styled to always show.
const SCROLL_SX = {
  flex: 1, minHeight: 0, overflow: 'auto',
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-track': { backgroundColor: '#1a1a1a' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#3a3a3a', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#4a4a4a' },
  scrollbarWidth: 'thin',
  scrollbarColor: '#3a3a3a #1a1a1a',
};
const EMPTY_SX = { color: '#555', fontSize: '0.78rem', textAlign: 'center', py: 1 };
const GRID_TH = {
  position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#151515',
  padding: '2px 5px', textAlign: 'center', fontWeight: 500,
  color: '#666', fontSize: '0.66rem', borderBottom: '1px solid #2a2a2a',
};
const GRID_TD = { padding: '2px 5px', textAlign: 'center', fontSize: '0.74rem' };

const LOGO_STYLE = { width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 };

const NUM_SX = {
  fontSize: '0.82rem',
  fontVariantNumeric: 'tabular-nums',
  fontFamily: 'monospace',
  whiteSpace: 'nowrap',
};

export default function MarketsModal({ open, onClose, markets, meta }) {
  const oddsFormat = useOddsFormat();
  // Books shown are the modal's own saved list, not the opening page's filter,
  // so the columns are the same no matter where you clicked from. Empty = all.
  const savedBooks = useMarketsBooks();
  const activeBooks = savedBooks.length ? savedBooks : null;
  // Everything pickable: every book with a logo plus every book quoting one
  // of these markets. Pick'em books never show here.
  const allBooks = useMemo(() => {
    const set = new Set(Object.keys(meta?.bookLogos || {}));
    (markets || []).forEach((m) => m.books.forEach((b) => set.add(b.book)));
    return [...set].filter((b) => !isPickemBook(b)).sort((a, b) => a.localeCompare(b));
  }, [markets, meta]);
  const [booksAnchor, setBooksAnchor] = useState(null);
  // Same book set as the alt grid, so the panel can't name a best book the
  // grid doesn't have a column for.
  const rows = useMemo(() => {
    const allowed = activeBooks ? new Set(activeBooks) : null;
    return (markets || [])
      .map((m) => (allowed ? summarizeMarket(m, allowed) : m))
      .filter((m) => m.book_count > 0);
  }, [markets, activeBooks]);
  const phone = useMediaQuery(PHONE_MQ);
  const narrow = useMediaQuery('(max-width:820px)') || phone;
  const [selectedKey, setSelectedKey] = useState(null);
  // Alt grids fetched this session, by market key. Once a market's grid is in,
  // its panel row ranks from the grid rather than from the snapshot taken at
  // click time, so the two panes quote the same prices.
  const [grids, setGrids] = useState({});
  const logoOf = useCallback((book) => meta?.bookLogos?.[book] || '', [meta]);
  // Scroll the selected market into view when the modal opens (the default is
  // the deepest market, which is rarely at the top of an alphabetical list).
  const didScroll = useRef(false);
  const selectedRowRef = useCallback((node) => {
    if (!node || didScroll.current) return;
    // The ref fires while the dialog is still animating in, when the list has
    // no height yet and scrollTop clamps to 0 — so retry after layout.
    let tries = 0;
    const place = () => {
      const box = node.closest('[data-market-list]');
      if (!box) return;
      if (box.clientHeight === 0 && tries < 10) { tries += 1; requestAnimationFrame(place); return; }
      box.scrollTop = Math.max(0, node.offsetTop - (box.clientHeight / 2) + (node.offsetHeight / 2));
      didScroll.current = true;
    };
    requestAnimationFrame(place);
  }, []);

  // Default to the deepest market (most books) — the one people came to look
  // at. The list itself stays alphabetical.
  useEffect(() => {
    const deepest = rows.reduce((acc, m) => (acc == null || m.book_count > acc.book_count ? m : acc), null);
    didScroll.current = false;
    setSelectedKey(deepest ? marketKey(deepest) : null);
    setGrids({});
  }, [meta, open]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Cached grids were fetched for the old book set.
  useEffect(() => { setGrids({}); }, [savedBooks]);

  if (!open) return null;

  const shownBooks = allBooks.filter((b) => !activeBooks || activeBooks.includes(b));
  const isChecked = (b) => !activeBooks || activeBooks.includes(b);
  const toggleBook = (b) => {
    const base = activeBooks || allBooks;
    const next = base.includes(b) ? base.filter((x) => x !== b) : [...base, b];
    if (!next.length) return;   // no books would mean an empty modal
    setMarketsBooks(allBooks.every((x) => next.includes(x)) ? [] : next);
  };

  const price = (p) => (p == null ? '-' : fmtPrice(p, oddsFormat));
  const selected = rows.find((m) => marketKey(m) === selectedKey) || rows[0] || null;
  const selectedGridKey = marketKey(selected);
  const onGrid = (d) => setGrids((g) => ({ ...g, [selectedGridKey]: d }));
  const bestOf = (m) => {
    const grid = grids[marketKey(m)];
    return grid ? bestFromGrid(m, grid, logoOf) : m.best;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={phone}
      PaperProps={{
        sx: phone ? PHONE_PAPER : {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          maxHeight: '85vh',
          maxWidth: narrow ? '560px' : 'min(1240px, 96vw)',
          borderRadius: '8px',
          border: '1px solid #333',
        },
      }}
    >
      {/* Narrow: a flex column so the alt grid scrolls inside its own box (header
          row pinned, main line centred) instead of the whole dialog scrolling. */}
      <DialogContent sx={{ p: 0, ...(narrow && { display: 'flex', flexDirection: 'column', overflow: 'hidden' }) }}>
        {/* ── Header ── */}
        <Box sx={{ px: phone ? 1.5 : 2.5, pt: phone ? 1.25 : 2, pb: phone ? 1 : 1.5, borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography sx={{ fontSize: '0.95rem', color: '#fff', flex: 1, minWidth: 0, pt: 0.25 }}>
              {meta?.title || ''}
            </Typography>

            {/* Book picker — the modal's own list, shared by every page */}
            {allBooks.length > 0 && (
              <Box onClick={(e) => setBooksAnchor(e.currentTarget)} sx={{
                flexShrink: 0, mt: 0.25,
                px: 1, py: phone ? 0.6 : 0.25, fontSize: phone ? '0.78rem' : '0.7rem',
                cursor: 'pointer', borderRadius: '3px',
                border: '1px solid #333', color: activeBooks ? '#ddd' : '#888',
                backgroundColor: activeBooks ? '#2a2a2a' : 'transparent',
                '&:hover': { color: '#fff', borderColor: '#555' },
                whiteSpace: 'nowrap',
              }}>
                Books · {shownBooks.length}/{allBooks.length}
              </Box>
            )}

            <IconButton onClick={onClose} size={phone ? 'medium' : 'small'}
              sx={{ flexShrink: 0, mt: phone ? -0.75 : -0.5, mr: -1, color: phone ? '#888' : '#555', '&:hover': { color: '#fff' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Popover
            open={Boolean(booksAnchor)}
            anchorEl={booksAnchor}
            onClose={() => setBooksAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { backgroundColor: '#1e1e1e', color: '#eee', border: '1px solid #333', mt: 0.5, minWidth: 200 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.8, borderBottom: '1px solid #2a2a2a' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Books</Typography>
              <Typography onClick={() => setMarketsBooks([])} sx={{
                fontSize: '0.7rem', cursor: 'pointer', color: activeBooks ? '#ccc' : '#555',
                '&:hover': { color: '#fff' },
              }}>All</Typography>
            </Box>
            <Box sx={{ maxHeight: 360, overflowY: 'auto', py: 0.5 }}>
              {allBooks.map((b) => (
                <Box key={b} onClick={() => toggleBook(b)} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5, pr: 1.5, cursor: 'pointer',
                  '&:hover': { backgroundColor: '#262626' },
                  '&:hover .only': { opacity: 1 },
                  // Touch screens have no hover: show "only" and give rows room
                  '@media (hover: none)': { py: 0.4, '& .only': { opacity: 1, px: 0.5 } },
                }}>
                  <Checkbox size="small" checked={isChecked(b)} tabIndex={-1} disableRipple
                    sx={{ p: 0.5, color: '#555', '&.Mui-checked': { color: '#ff1744' } }} />
                  {meta?.bookLogos?.[b] && <img src={meta.bookLogos[b]} alt="" style={LOGO_STYLE} />}
                  <Typography sx={{ fontSize: '0.78rem', flex: 1, whiteSpace: 'nowrap' }}>{b}</Typography>
                  <Typography className="only" onClick={(e) => { e.stopPropagation(); setMarketsBooks([b]); }} sx={{
                    fontSize: '0.65rem', color: '#777', opacity: 0, '&:hover': { color: '#fff' },
                  }}>only</Typography>
                </Box>
              ))}
            </Box>
          </Popover>

          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
            {meta?.league && (
              <>
                <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{meta.league}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#444', mx: 0.3 }}>|</Typography>
              </>
            )}
            {meta?.awayTeamLogo && (
              <img src={meta.awayTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
            )}
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta?.awayTeam}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>@</Typography>
            {meta?.homeTeamLogo && (
              <img src={meta.homeTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
            )}
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta?.homeTeam}</Typography>
            {/* Phone: start time on this line, saving a row for the grid */}
            {phone && meta?.eventStart && (
              <Typography sx={{ fontSize: '0.72rem', color: '#666', ml: 0.5 }}>
                · {fmtEventStart(meta.eventStart)}
              </Typography>
            )}
          </Box>

          {!phone && meta?.eventStart && (
            <Typography sx={{ fontSize: '0.72rem', color: '#666', mt: 0.3 }}>
              {fmtEventStart(meta.eventStart)}
            </Typography>
          )}
        </Box>

        {/* ── Content: market list on the left, alt grid on the right ── */}
        {rows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>No markets available</Typography>
          </Box>
        ) : narrow ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Narrow: market picker above the grid */}
            <Box sx={{ px: phone ? 1 : 2, py: phone ? 0.75 : 1, borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
              <select
                value={selectedKey || ''}
                onChange={(e) => setSelectedKey(e.target.value)}
                // 16px: iOS zooms the page when a smaller control is focused
                style={phone ? { ...SELECT_STYLE, fontSize: 16, padding: '8px 10px' } : SELECT_STYLE}
              >
                {rows.map((m) => (
                  <option key={marketKey(m)} value={marketKey(m)}>
                    {shortMarket(m.market)}{m.line == null ? '' : ` ${m.line}`} — {m.book_count} book{m.book_count === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </Box>
            {selected && <MarketDetail market={selected} meta={meta} books={activeBooks} price={price} onGrid={onGrid} />}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', height: 'min(620px, 70vh)' }}>
            {/* Market list */}
            <Box data-market-list sx={{ width: 196, flexShrink: 0, borderRight: '1px solid #2a2a2a', overflowY: 'auto' }}>
              {rows.map((m) => {
                const key = marketKey(m);
                const isSel = key === marketKey(selected);
                return (
                  <Box key={key} ref={isSel ? selectedRowRef : null}
                    onClick={() => setSelectedKey(key)} sx={{
                    px: 1.5, py: 0.9, cursor: 'pointer',
                    borderBottom: '1px solid #202020',
                    borderLeft: isSel ? '2px solid #ff1744' : '2px solid transparent',
                    backgroundColor: isSel ? '#232323' : 'transparent',
                    '&:hover': { backgroundColor: isSel ? '#232323' : '#1f1f1f' },
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem', color: isSel ? '#fff' : '#ccc',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={m.market}>
                      {shortMarket(m.market)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#666' }}>
                      {m.line == null ? '' : `${m.line} · `}
                      {m.book_count} book{m.book_count === 1 ? '' : 's'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mt: 0.2 }}>
                      {bestOf(m).slice(0, 2).map((b) => (b.price == null ? null : (
                        <Box key={b.side} sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}
                          title={`${b.side} — ${b.books.join(', ')}`}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#666' }}>{b.side.charAt(0)}</Typography>
                          <Typography sx={{ ...NUM_SX, fontSize: '0.7rem', color: '#bbb' }}>{price(b.price)}</Typography>
                          <BestLogos logos={b.logos} />
                        </Box>
                      )))}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Selected market — header pinned, grid scrolls inside */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {selected && <MarketDetail market={selected} meta={meta} books={activeBooks} price={price} onGrid={onGrid} />}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SUB_TH = {
  textAlign: 'left',
  padding: '3px 6px',
  color: '#555',
  fontSize: '0.66rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const TD_STYLE = {
  padding: '3px 6px',
  fontSize: '0.78rem',
};

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace', whiteSpace: 'nowrap' };